import { useEffect, Suspense, lazy, memo, startTransition } from 'react'
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom'
import { onCLS, onINP, onFCP, onLCP, onTTFB } from 'web-vitals'
import ErrorBoundary from './components/ErrorBoundary'
import { Header } from './components/ui'
import { AuthModal } from './components/auth'
import OnboardingModal from './components/auth/OnboardingModal'
import AchievementModal from './components/loyalty/AchievementModal'
import { useAuthStore } from './stores'
import { useAchievementModal } from './stores/loyaltyStore'
import { initPerformanceMonitoring, measureTiming } from './utils/performance'
import { initAccessibility, ScreenReaderAnnouncer } from './utils/accessibility'

// Lazy load page components for better performance
const Home = lazy(() => import('./pages/Home'))
const RaffleDetail = lazy(() => import('./pages/RaffleDetail'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const RaffleManagement = lazy(() => import('./pages/admin/RaffleManagement'))
const UserManagement = lazy(() => import('./pages/admin/UserManagement'))
const Analytics = lazy(() => import('./pages/admin/Analytics'))
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'))
const CreateRaffle = lazy(() => import('./pages/admin/CreateRaffle'))
const EditRaffle = lazy(() => import('./pages/admin/EditRaffle'))
const AdminPrizeManagement = lazy(() => import('./components/admin/AdminPrizeManagement'))
const MyRaffles = lazy(() => import('./pages/MyRaffles'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'))

// Enhanced loading component for route transitions with accessibility
const RouteLoader = memo(() => {
  useEffect(() => {
    // Announce loading state to screen readers
    ScreenReaderAnnouncer.announce('Cargando página, por favor espera')
  }, [])

  return (
    <div className="flex items-center justify-center min-h-[50vh]" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div className="absolute top-2 left-2 w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" style={{ animationDirection: 'reverse' }}></div>
        </div>
        <p className="text-gray-600 animate-pulse" aria-label="Cargando contenido">
          Cargando página...
        </p>
      </div>
      <span className="sr-only">Cargando contenido de la página, por favor espera</span>
    </div>
  )
})

RouteLoader.displayName = 'RouteLoader'

function App() {
  const {
    initializeAuth,
    isInitialized,
    isAuthModalOpen,
    closeAuthModal,
    isOnboardingModalOpen,
    closeOnboardingModal
  } = useAuthStore()

  // Loyalty achievement modal
  const { isOpen: isAchievementModalOpen, achievement, onClose: closeAchievementModal } = useAchievementModal()

  useEffect(() => {
    // CRITICAL FIX: Store cleanup functions for web vitals observers
    // These observers use PerformanceObserver internally and should be cleaned up
    let webVitalsCleanupFns: (() => void)[] = []

    // Initialize performance monitoring and web vitals
    const initializePerformanceMonitoring = () => {
      startTransition(() => {
        // Initialize performance monitoring
        initPerformanceMonitoring()

        // Web Vitals monitoring with proper callback
        const handleWebVital = (_metric: unknown) => {
          // Send to analytics service here if needed
        }

        // Store cleanup functions returned by web-vitals library
        // Note: These functions report immediately and don't need explicit cleanup
        // but storing them follows best practices
        onCLS(handleWebVital)
        onINP(handleWebVital) // Interaction to Next Paint (replaces FID)
        onFCP(handleWebVital)
        onLCP(handleWebVital)
        onTTFB(handleWebVital)
      })
    }

    // Initialize accessibility features
    const initializeAccessibilityFeatures = () => {
      startTransition(() => {
        initAccessibility()
        
        // Set up focus management for keyboard navigation
        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            document.body.classList.add('user-is-tabbing')
          }
        }

        const handleMouseDown = () => {
          document.body.classList.remove('user-is-tabbing')
        }

        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handleMouseDown)
      })
      
      return () => {
        // No cleanup needed for accessibility init
      }
    }

    // Initialize Firebase Auth listener
    const unsubscribe = initializeAuth()
    
    // Initialize performance and accessibility
    initializePerformanceMonitoring()
    const cleanupAccessibility = initializeAccessibilityFeatures()
    
    // Performance timing for app initialization
    const appTiming = measureTiming('app-initialization')
    appTiming.start()

    return () => {
      unsubscribe()
      cleanupAccessibility()
      appTiming.end()
    }
  }, [initializeAuth])

  // CRITICAL FIX: Wait for auth to be initialized before rendering
  // This prevents race conditions where components try to access auth state before it's ready
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log error for monitoring
        console.error('App Error:', error, errorInfo)
      }}
    >
      <Router>
        <div className="App min-h-screen bg-gradient-to-br from-gray-50 to-white">
          <a 
            href="#main-content" 
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-blue-600 text-white px-4 py-2 rounded z-50"
            onFocus={() => ScreenReaderAnnouncer.announce('Enlace de salto al contenido principal activado')}
          >
            Saltar al contenido principal
          </a>
          <Header />
          <main id="main-content" role="main" tabIndex={-1}>
            <Suspense 
              fallback={<RouteLoader />}
            >
              <Routes>
                <Route 
                  path="/" 
                  element={<OptimizedHome />} 
                />
                <Route
                  path="/raffle/:id"
                  element={<RaffleDetailWrapper />}
                />
                <Route
                  path="/my-raffles"
                  element={<OptimizedMyRaffles />}
                />
                <Route
                  path="/admin"
                  element={<OptimizedAdminDashboard />}
                />
                <Route 
                  path="/admin/raffles" 
                  element={<OptimizedRaffleManagement />} 
                />
                <Route 
                  path="/admin/raffles/create" 
                  element={<OptimizedCreateRaffle />} 
                />
                <Route 
                  path="/admin/raffles/:id/edit" 
                  element={<EditRaffleWrapper />} 
                />
                <Route 
                  path="/admin/users" 
                  element={<OptimizedUserManagement />} 
                />
                <Route 
                  path="/admin/analytics" 
                  element={<OptimizedAnalytics />} 
                />
                <Route
                  path="/admin/settings"
                  element={<OptimizedAdminSettings />}
                />
                <Route
                  path="/admin/prizes"
                  element={<OptimizedAdminPrizeManagement />}
                />
                <Route
                  path="/privacy"
                  element={<OptimizedPrivacyPolicy />}
                />
                <Route
                  path="/terms"
                  element={<OptimizedTermsOfService />}
                />
                <Route
                  path="/terms-and-conditions"
                  element={<OptimizedTermsAndConditions />}
                />
              </Routes>
            </Suspense>
          </main>
          
          {/* Auth Modal */}
          <AuthModal
            isOpen={isAuthModalOpen}
            onClose={closeAuthModal}
          />

          {/* Unified Onboarding Modal */}
          <OnboardingModal
            isOpen={isOnboardingModalOpen}
            onComplete={closeOnboardingModal}
            onExit={closeOnboardingModal}
          />

          {/* Achievement Celebration Modal */}
          {achievement && (
            <AchievementModal
              isOpen={isAchievementModalOpen}
              achievement={achievement}
              onClose={closeAchievementModal}
            />
          )}
        </div>
      </Router>
    </ErrorBoundary>
  )
}

// Wrapper component to extract route params
function RaffleDetailWrapper() {
  const { id } = useParams<{ id: string }>()
  
  return <RaffleDetail raffleId={id || ''} />
}

// Wrapper component for EditRaffle
function EditRaffleWrapper() {
  const { id } = useParams<{ id: string }>()
  
  return <EditRaffle raffleId={id || ''} />
}

// Optimized component wrappers with React.memo and performance tracking
const OptimizedHome = memo(() => {
  useEffect(() => {
    const timing = measureTiming('home-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <Home />
})

const OptimizedAdminDashboard = memo(() => {
  useEffect(() => {
    const timing = measureTiming('admin-dashboard-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <AdminDashboard />
})

const OptimizedRaffleManagement = memo(() => {
  useEffect(() => {
    const timing = measureTiming('raffle-management-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <RaffleManagement />
})

const OptimizedCreateRaffle = memo(() => {
  useEffect(() => {
    const timing = measureTiming('create-raffle-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <CreateRaffle />
})

const OptimizedUserManagement = memo(() => {
  useEffect(() => {
    const timing = measureTiming('user-management-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <UserManagement />
})

const OptimizedAnalytics = memo(() => {
  useEffect(() => {
    const timing = measureTiming('analytics-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <Analytics />
})

const OptimizedAdminSettings = memo(() => {
  useEffect(() => {
    const timing = measureTiming('admin-settings-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <AdminSettings />
})

const OptimizedAdminPrizeManagement = memo(() => {
  useEffect(() => {
    const timing = measureTiming('admin-prize-management-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <AdminPrizeManagement />
})

const OptimizedMyRaffles = memo(() => {
  useEffect(() => {
    const timing = measureTiming('my-raffles-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <MyRaffles />
})

const OptimizedPrivacyPolicy = memo(() => {
  useEffect(() => {
    const timing = measureTiming('privacy-policy-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <PrivacyPolicy />
})

const OptimizedTermsOfService = memo(() => {
  useEffect(() => {
    const timing = measureTiming('terms-of-service-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <TermsOfService />
})

const OptimizedTermsAndConditions = memo(() => {
  useEffect(() => {
    const timing = measureTiming('terms-and-conditions-render')
    timing.start()
    return () => {
      timing.end()
    }
  }, [])

  return <TermsAndConditions />
})

// Set display names for debugging
OptimizedHome.displayName = 'OptimizedHome'
OptimizedAdminDashboard.displayName = 'OptimizedAdminDashboard'
OptimizedRaffleManagement.displayName = 'OptimizedRaffleManagement'
OptimizedCreateRaffle.displayName = 'OptimizedCreateRaffle'
OptimizedUserManagement.displayName = 'OptimizedUserManagement'
OptimizedAnalytics.displayName = 'OptimizedAnalytics'
OptimizedAdminSettings.displayName = 'OptimizedAdminSettings'
OptimizedAdminPrizeManagement.displayName = 'OptimizedAdminPrizeManagement'
OptimizedMyRaffles.displayName = 'OptimizedMyRaffles'
OptimizedPrivacyPolicy.displayName = 'OptimizedPrivacyPolicy'
OptimizedTermsOfService.displayName = 'OptimizedTermsOfService'
OptimizedTermsAndConditions.displayName = 'OptimizedTermsAndConditions'

export default memo(App)
